import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { 
  PlusIcon,
  TrashIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { leadService } from '../../services/leadService';
import { campaignService } from '../../services/campaignService';
import { Campaign } from '../../types/campaign';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Breadcrumb from '../../components/UI/Breadcrumb';
import Button from '../../components/UI/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import ErrorMessage from '../../components/UI/ErrorMessage';
import { SectionHeader } from '../../components/UI/SectionHeader';

const addLeadSchema = z.object({
  full_name: z.string().min(1, 'Full name is required'),
  phone_e164: z.string()
    .min(1, 'Phone number is required')
    .regex(/^\+[1-9]\d{1,14}$/, 'Please enter a valid phone number in E.164 format (e.g., +15551234567)'),
  extra_phones: z.array(z.object({
    value: z.string().regex(/^\+[1-9]\d{1,14}$/, 'Invalid E.164 phone number').or(z.literal('')),
  })).optional(),
  email: z.string().email('Please enter a valid email address').optional().or(z.literal('')),
  company_name: z.string().optional().or(z.literal('')),
  custom_fields: z.array(z.object({
    key: z.string().min(1, 'Field name is required'),
    value: z.string().min(1, 'Field value is required')
  })).optional()
});

type AddLeadFormData = z.infer<typeof addLeadSchema>;

const AddLeadPage: React.FC = () => {
  const { id: campaignId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<AddLeadFormData>({
    resolver: zodResolver(addLeadSchema),
    defaultValues: {
      full_name: '',
      phone_e164: '',
      extra_phones: [],
      email: '',
      company_name: '',
      custom_fields: []
    }
  });

  const { fields: extraPhoneFields, append: appendPhone, remove: removePhone } = useFieldArray({
    control,
    name: 'extra_phones'
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'custom_fields'
  });

  useEffect(() => {
    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      const campaignData = await campaignService.getCampaign(campaignId!);
      setCampaign(campaignData);
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
      // TODO: Show error toast
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: AddLeadFormData) => {
    if (!campaignId) return;

    try {
      setSubmitting(true);
      
      // Convert custom fields array to object
      const customFields = data.custom_fields?.reduce((acc, field) => {
        if (field.key && field.value) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {} as Record<string, string>) || {};

      const extraNums = (data.extra_phones || [])
        .map((p) => p.value)
        .filter((v) => v && /^\+[1-9]\d{1,14}$/.test(v));
      const allPhones = [data.phone_e164, ...extraNums];

      const leadData = {
        fullName: data.full_name,
        phoneE164: data.phone_e164,
        email: data.email || undefined,
        companyName: data.company_name || undefined,
        custom: Object.keys(customFields).length > 0 ? customFields : undefined,
        phoneNumbers: allPhones.length > 1 ? allPhones : undefined,
      };

      await leadService.createLead(campaignId, leadData);
      
      toast.success('Lead added successfully!');
      navigate(`/campaigns/${campaignId}/leads`);
    } catch (error) {
      console.error('Failed to add lead:', error);
      
      // Show user-friendly error message
      let errorMessage = 'Failed to add lead. Please try again.';
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const apiError = error as any;
        // Backend sends: { error: { message: "...", code: "...", statusCode: ... } }
        if (apiError.response?.data?.error?.message) {
          errorMessage = apiError.response.data.error.message;
        } else if (apiError.response?.data?.message) {
          errorMessage = apiError.response.data.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const addCustomField = () => {
    append({ key: '', value: '' });
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // If it starts with 1 and has 11 digits, format as US number
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1${digits.slice(1)}`;
    }
    
    // If it has 10 digits, assume US number
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    
    // If it already starts with +, keep as is
    if (value.startsWith('+')) {
      return value;
    }
    
    // Otherwise, add + prefix
    return digits ? `+${digits}` : value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-semibold text-foreground">Campaign not found</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          The campaign you're looking for doesn't exist or you don't have access to it.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Campaigns', href: '/campaigns' },
          { label: campaign.name, href: `/campaigns/${campaignId}` },
          { label: 'Leads', href: `/campaigns/${campaignId}/leads` },
          { label: 'Add Lead', current: true }
        ]}
      />
      
      {/* Header */}
      <Card>
        <CardContent className="p-6">
          <h1 className="text-2xl font-bold text-foreground">Add New Lead</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Add a new lead to <span className="font-medium">{campaign.name}</span> campaign.
          </p>
        </CardContent>
      </Card>

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-5 sm:p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <SectionHeader title="Basic Information" />
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium text-foreground">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    {...register('full_name')}
                    className="input"
                    placeholder="Enter full name"
                  />
                  <ErrorMessage message={errors.full_name?.message} />
                </div>

                <div>
                  <label htmlFor="phone_e164" className="block text-sm font-medium text-foreground">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone_e164"
                    {...register('phone_e164', {
                      onChange: (e) => {
                        e.target.value = formatPhoneNumber(e.target.value);
                      }
                    })}
                    className="input"
                    placeholder="+15551234567"
                  />
                  <ErrorMessage message={errors.phone_e164?.message} />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Enter in E.164 format (e.g., +15551234567)
                  </p>
                </div>

                {/* Extra phone numbers */}
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-foreground">
                      Additional Phone Numbers
                    </label>
                    <Button
                      type="button"
                      onClick={() => appendPhone({ value: '' })}
                      variant="outlined"
                      size="sm"
                    >
                      <PlusIcon className="-ml-0.5 mr-1 h-4 w-4" />
                      Add Number
                    </Button>
                  </div>
                  {extraPhoneFields.map((field, index) => (
                    <div key={field.id} className="flex items-center gap-2 mb-2">
                      <input
                        type="tel"
                        {...register(`extra_phones.${index}.value`, {
                          onChange: (e) => {
                            e.target.value = formatPhoneNumber(e.target.value);
                          }
                        })}
                        className="input flex-1"
                        placeholder="+15559876543"
                      />
                      <button
                        type="button"
                        onClick={() => removePhone(index)}
                        className="text-destructive hover:text-destructive/80 p-1"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    {...register('email')}
                    className="input"
                    placeholder="Enter email address (optional)"
                  />
                  <ErrorMessage message={errors.email?.message} />
                </div>

                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-foreground">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company_name"
                    {...register('company_name')}
                    className="input"
                    placeholder="Enter company name (optional)"
                  />
                  <ErrorMessage message={errors.company_name?.message} />
                </div>
              </div>
            </div>

            {/* Custom Fields */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">
                  Custom Fields
                </h3>
                <Button
                  type="button"
                  onClick={addCustomField}
                  variant="outlined"
                  size="sm"
                >
                  <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                  Add Field
                </Button>
              </div>
              
              {fields.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
                  <div className="mx-auto h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center">
                <UserPlusIcon className="h-6 w-6 text-primary" />
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    No custom fields added yet.
                  </p>
                  <Button
                    type="button"
                    onClick={addCustomField}
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                  >
                    Add your first custom field
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div key={field.id} className="flex items-start space-x-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          {...register(`custom_fields.${index}.key`)}
                          className="input"
                          placeholder="Field name (e.g., Company)"
                        />
                        <ErrorMessage message={errors.custom_fields?.[index]?.key?.message} />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          {...register(`custom_fields.${index}.value`)}
                          className="input"
                          placeholder="Field value (e.g., Acme Corp)"
                        />
                        <ErrorMessage message={errors.custom_fields?.[index]?.value?.message} />
                      </div>
                      <Button
                        type="button"
                        onClick={() => remove(index)}
                        variant="ghost"
                        size="sm"
                        className="mt-1 p-2 text-destructive hover:text-destructive/80"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Campaign Info */}
            {campaign.captureSchema && campaign.captureSchema.length > 0 && (
              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">
                  Campaign Capture Fields
                </h4>
                <p className="text-sm text-muted-foreground mb-3">
                  This campaign is configured to capture the following information during calls:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {campaign.captureSchema.map((field) => (
                    <div key={field.name} className="text-sm">
                      <span className="font-medium text-foreground capitalize">
                        {field.label || field.name.replace('_', ' ')}
                      </span>
                      <span className="text-muted-foreground ml-1">
                        ({field.type})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-8 flex items-center justify-end space-x-3">
            <Button
              type="button"
              onClick={() => navigate(`/campaigns/${campaignId}/leads`)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              variant="primary"
              loading={submitting}
            >
              <UserPlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              {submitting ? 'Adding Lead...' : 'Add Lead'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddLeadPage;